import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { ArrowLeft, Trash2, FileText, Search, FolderOpen, Clock, User, ChevronRight, Languages, Download } from "lucide-react";
import { Input } from "./components/ui/input";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { key: "PC N°", label: "PC N°" },
  { key: "PC EXPRESS N°", label: "PC EXPRESS N°" },
  { key: "BON TECHNIQUE N°", label: "BON TECHNIQUE N°" },
  { key: "BON DE LIVRAISON N°", label: "BON DE LIVRAISON N°" },
];

function SavedSheets() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sheets, setSheets] = useState([]);
  const [activeCategory, setActiveCategory] = useState("PC N°");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSheets();
  }, []);

  const fetchSheets = async () => {
    try {
      const response = await fetch(`${API}/sheets`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSheets(data.sheets || []);
      }
    } catch (error) {
      console.error("Error fetching sheets:", error);
      toast.error("Erreur lors du chargement des fiches");
    } finally {
      setLoading(false);
    }
  };

  const deleteSheet = async (sheetId, sheetName) => {
    if (!window.confirm(`Supprimer la fiche "${sheetName}" ?`)) return;
    try {
      const response = await fetch(`${API}/sheets/${sheetId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        toast.success("Fiche supprimée");
        fetchSheets();
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const loadSheet = (sheetId) => {
    navigate(`/?load=${sheetId}`);
  };

  const duplicateAsExport = async (sheet) => {
    try {
      const detail = await fetch(`${API}/sheets/${sheet.sheet_id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!detail.ok) throw new Error("load failed");
      const data = await detail.json();
      const sourceData = data.sheet?.form_data || sheet.form_data || {};
      const exportData = { ...sourceData, sheet_language: 'EN' };
      // Pas de code prix à l'export
      if (Array.isArray(exportData.formats)) {
        exportData.formats = exportData.formats.map(f => ({ ...f, prix: "" }));
      }

      const body = new FormData();
      body.append('name', `${sheet.name} (EXPORT)`);
      body.append('category', sheet.category);
      body.append('form_data', JSON.stringify(exportData));

      const response = await fetch(`${API}/sheets`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body
      });
      if (response.ok) {
        toast.success("Fiche dupliquée en modèle Export");
        fetchSheets();
      } else {
        toast.error("Erreur lors de la duplication");
      }
    } catch (error) {
      console.error("Error duplicating sheet:", error);
      toast.error("Erreur lors de la duplication");
    }
  };

  const filteredSheets = sheets.filter(s => {
    const matchesCategory = s.category === activeCategory;
    const matchesSearch = searchQuery === "" || 
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (s.form_data?.artist || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (s.form_data?.album || "").toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getCategoryCount = (catKey) => sheets.filter(s => s.category === catKey).length;

  const formatDate = (isoDate) => {
    if (!isoDate) return "";
    const d = new Date(isoDate);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-lime-400" style={{ fontFamily: 'Raleway, sans-serif' }}>
                Fiches enregistrées
              </h1>
              <p className="text-gray-400 mt-1">{sheets.length} fiche{sheets.length !== 1 ? 's' : ''} au total</p>
            </div>
            <Button onClick={() => navigate('/')} variant="outline" className="gap-2 border-lime-400 text-lime-400 hover:bg-lime-400 hover:text-black">
              <ArrowLeft className="h-4 w-4" />
              Retour au formulaire
            </Button>
          </div>

          {/* Category Tabs */}
          <div className="mb-6">
            <div className="flex gap-1 bg-gray-800/50 p-1 rounded-lg border border-gray-700">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.key}
                  data-testid={`tab-${cat.key.replace(/[^a-zA-Z]/g, '').toLowerCase()}`}
                  onClick={() => setActiveCategory(cat.key)}
                  className={`flex-1 px-4 py-3 rounded-md text-sm font-semibold transition-all duration-200 ${
                    activeCategory === cat.key
                      ? 'bg-lime-400 text-black shadow-lg shadow-lime-400/20'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                  }`}
                >
                  {cat.label}
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                    activeCategory === cat.key
                      ? 'bg-black/20 text-black'
                      : 'bg-gray-700 text-gray-400'
                  }`}>
                    {getCategoryCount(cat.key)}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-6 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              data-testid="search-sheets"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher par nom, artiste ou album..."
              className="pl-10 h-11 bg-gray-800/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-lime-400"
            />
          </div>

          {/* Sheets Table */}
          <Card className="bg-gray-800/30 border-gray-700">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lime-400"></div>
                </div>
              ) : filteredSheets.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                  <FolderOpen className="h-12 w-12 mb-3 opacity-50" />
                  <p className="text-lg font-medium">Aucune fiche dans cette catégorie</p>
                  <p className="text-sm mt-1">Les fiches enregistrées apparaitront ici</p>
                </div>
              ) : (
                <div className="overflow-hidden rounded-lg">
                  {/* Table Header */}
                  <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-900/50 border-b border-gray-700 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    <div className="col-span-4">Nom de la fiche</div>
                    <div className="col-span-2">Artiste</div>
                    <div className="col-span-1">Modèle</div>
                    <div className="col-span-2">Créé par</div>
                    <div className="col-span-2">Date</div>
                    <div className="col-span-1 text-right">Actions</div>
                  </div>
                  
                  {/* Table Rows */}
                  {filteredSheets.map((sheet) => (
                    <div 
                      key={sheet.sheet_id}
                      data-testid={`sheet-row-${sheet.sheet_id}`}
                      className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-gray-800 hover:bg-gray-700/30 transition-colors cursor-pointer group"
                      onClick={() => loadSheet(sheet.sheet_id)}
                    >
                      <div className="col-span-4 flex items-center gap-3">
                        <FileText className="h-5 w-5 text-lime-400 flex-shrink-0" />
                        <span className="text-white font-medium truncate">{sheet.name}</span>
                        <ChevronRight className="h-4 w-4 text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <div className="col-span-2 flex items-center text-gray-300 text-sm truncate">
                        {sheet.form_data?.artist || "-"}
                      </div>
                      <div className="col-span-1 flex items-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${sheet.form_data?.sheet_language === 'EN' ? 'bg-blue-500/20 text-blue-300' : 'bg-lime-400/20 text-lime-300'}`}>
                          {sheet.form_data?.sheet_language === 'EN' ? 'EXPORT' : 'FR'}
                        </span>
                      </div>
                      <div className="col-span-2 flex items-center gap-1 text-gray-400 text-sm">
                        <User className="h-3 w-3" />
                        {sheet.created_by || "-"}
                      </div>
                      <div className="col-span-2 flex items-center gap-1 text-gray-400 text-sm">
                        <Clock className="h-3 w-3" />
                        {formatDate(sheet.created_at)}
                      </div>
                      <div className="col-span-1 flex items-center justify-end gap-1">
                        <Button
                          data-testid={`download-pdf-${sheet.sheet_id}`}
                          variant="ghost"
                          size="sm"
                          title="Télécharger le bon en PDF"
                          onClick={(e) => { e.stopPropagation(); navigate(`/?load=${sheet.sheet_id}&print=1`); }}
                          className="h-8 w-8 p-0 text-gray-500 hover:text-lime-300 hover:bg-lime-400/10"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        {sheet.form_data?.sheet_language !== 'EN' && (
                          <Button
                            data-testid={`duplicate-export-${sheet.sheet_id}`}
                            variant="ghost"
                            size="sm"
                            title="Dupliquer en modèle Export"
                            onClick={(e) => { e.stopPropagation(); duplicateAsExport(sheet); }}
                            className="h-8 w-8 p-0 text-gray-500 hover:text-blue-300 hover:bg-blue-400/10"
                          >
                            <Languages className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          data-testid={`delete-sheet-${sheet.sheet_id}`}
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); deleteSheet(sheet.sheet_id, sheet.name); }}
                          className="h-8 w-8 p-0 text-gray-500 hover:text-red-400 hover:bg-red-400/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      <Toaster position="top-right" />
    </div>
  );
}

export default SavedSheets;
